import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { useAuth, authFetch } from "../context/AuthContext";
import { 
  Shield, 
  MessageSquare, 
  Users, 
  Shirt, 
  Calendar,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
  Eye,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Admin() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [adminNotes, setAdminNotes] = useState("");
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUsers, setShowUsers] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/admin/check`);
        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.is_admin);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("Admin check failed:", error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, []);

  // Fetch admin stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  // Fetch feedback
  const fetchFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    try {
      const statusParam = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await authFetch(`${API_URL}/api/admin/feedback${statusParam}`);
      if (response.ok) {
        const data = await response.json();
        setFeedback(data.feedback || []);
      }
    } catch (error) {
      console.error("Failed to fetch feedback:", error);
    } finally {
      setFeedbackLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchFeedback();
    }
  }, [isAdmin, fetchStats, fetchFeedback]);

  // Update feedback status
  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/feedback/${feedbackId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          status: newStatus,
          admin_notes: adminNotes || undefined
        }),
      });
      
      if (response.ok) {
        toast.success(`Feedback marked as ${newStatus}`);
        fetchFeedback();
        fetchStats();
        setSelectedFeedback(null);
        setAdminNotes("");
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      toast.error("Failed to update feedback");
    }
  };

  // Delete feedback
  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm("Are you sure you want to delete this feedback?")) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/feedback/${feedbackId}`, {
        method: "DELETE",
      });
      
      if (response.ok) {
        toast.success("Feedback deleted");
        fetchFeedback();
        fetchStats();
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      toast.error("Failed to delete feedback");
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/users`);
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched users:", data);
        setUsers(data.users || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to fetch users:", response.status, errorData);
        toast.error(errorData.detail || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setUsersLoading(false);
    }
  };

  // Delete user
  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}? This will remove ALL their data permanently.`)) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/users/${userId}`, {
        method: "DELETE",
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`User deleted. Removed ${data.deleted.wardrobe_items} items, ${data.deleted.daily_logs} logs.`);
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Delete failed");
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete user");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "resolved":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" /> Resolved</Badge>;
      case "in_progress":
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" /> In Progress</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><AlertCircle className="w-3 h-3 mr-1" /> New</Badge>;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  // Not admin - redirect
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-page">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2 flex items-center gap-3">
              <Shield className="w-8 h-8 text-terracotta-500" />
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage feedback and monitor app usage
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => { fetchStats(); fetchFeedback(); }}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.users.total}</p>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-2">+{stats.users.recent_signups} this week</p>
              </CardContent>
            </Card>

            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.feedback.new}</p>
                    <p className="text-xs text-muted-foreground">New Feedback</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{stats.feedback.total} total</p>
              </CardContent>
            </Card>

            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Shirt className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.content.wardrobe_items}</p>
                    <p className="text-xs text-muted-foreground">Wardrobe Items</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.content.daily_logs}</p>
                    <p className="text-xs text-muted-foreground">Daily Logs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* User Tiers */}
        {stats && (
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium">
                User Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <div className="flex gap-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-muted-foreground">{stats.users.free}</p>
                  <p className="text-sm text-muted-foreground">Starter</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">{stats.users.plus}</p>
                  <p className="text-sm text-muted-foreground">Plus</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600">{stats.users.pro}</p>
                  <p className="text-sm text-muted-foreground">Pro</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Management Section */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
                <Users className="w-5 h-5" />
                User Management
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowUsers(!showUsers);
                  if (!showUsers && users.length === 0) {
                    fetchUsers();
                  }
                }}
                className="rounded-sm"
              >
                {showUsers ? "Hide Users" : "View All Users"}
              </Button>
            </div>
          </CardHeader>
          {showUsers && (
            <CardContent className="p-6 pt-0">
              {usersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No users found</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-end mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchUsers}
                      className="text-muted-foreground"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>
                  {users.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-4 bg-sand-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Users className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{u.name || "Unknown"}</p>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                          <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                            <span>{u.items_count} items</span>
                            <span>{u.logs_count} logs</span>
                            <span className={`font-medium ${
                              u.subscription?.tier === 'pro' ? 'text-purple-600' :
                              u.subscription?.tier === 'plus' ? 'text-blue-600' : ''
                            }`}>
                              {u.subscription?.tier === 'pro' ? 'Pro' :
                               u.subscription?.tier === 'plus' ? 'Plus' : 'Starter'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {u.created_at ? formatDate(u.created_at) : "N/A"}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteUser(u.user_id, u.email)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-user-${u.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Feedback Section */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                User Feedback
              </CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {feedbackLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : feedback.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No feedback yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedback.map((item) => (
                  <div 
                    key={item.feedback_id}
                    className="p-4 border border-border rounded-lg hover:bg-sand-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{item.user_name}</span>
                          <span className="text-xs text-muted-foreground">({item.user_email})</span>
                          {getStatusBadge(item.status)}
                        </div>
                        <p className="text-sm text-foreground line-clamp-2 mb-2">
                          {item.message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(item.created_at)}
                        </p>
                        {item.admin_notes && (
                          <p className="text-xs text-blue-600 mt-1 italic">
                            Note: {item.admin_notes}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedFeedback(item);
                            setAdminNotes(item.admin_notes || "");
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteFeedback(item.feedback_id)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Detail Modal */}
        <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Feedback Details</DialogTitle>
              <DialogDescription>
                Review and manage this feedback submission
              </DialogDescription>
            </DialogHeader>
            {selectedFeedback && (
              <div className="space-y-4">
                <div className="p-4 bg-sand-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium">{selectedFeedback.user_name}</p>
                      <p className="text-sm text-muted-foreground">{selectedFeedback.user_email}</p>
                    </div>
                    {getStatusBadge(selectedFeedback.status)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(selectedFeedback.created_at)}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Message</label>
                  <div className="p-3 bg-background border border-border rounded-lg whitespace-pre-wrap text-sm">
                    {selectedFeedback.message}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Admin Notes</label>
                  <Textarea
                    placeholder="Add notes about this feedback..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="min-h-[80px]"
                  />
                </div>

                <div className="flex justify-between pt-2">
                  <Button
                    variant="outline"
                    onClick={() => deleteFeedback(selectedFeedback.feedback_id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                  <div className="flex gap-2">
                    {selectedFeedback.status !== "in_progress" && (
                      <Button
                        variant="outline"
                        onClick={() => updateFeedbackStatus(selectedFeedback.feedback_id, "in_progress")}
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        In Progress
                      </Button>
                    )}
                    {selectedFeedback.status !== "resolved" && (
                      <Button
                        onClick={() => updateFeedbackStatus(selectedFeedback.feedback_id, "resolved")}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default Admin;
